import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "store";
import { useWindowDimensions } from "resources/BrowserManager";

import {
  SearchOutlined,
  DeleteOutlined,
  CrownFilled,
  FilterFilled,
} from "@ant-design/icons";

import {
  theme,
  Flex,
  Space,
  Typography,
  Collapse,
  Button,
  Input,
  DatePicker,
  InputNumber,
  Select,
  Tooltip,
} from "antd";
const { Text } = Typography;
const { RangePicker } = DatePicker;

import { IMaskInput } from "react-imask";
import dayjs from "dayjs";

import { getUnixFromDate } from "../../../shared/utilities";

import { TooltipFeatureUnavailable } from "components/Shared/CfdiElements";

const InputUuid = ({
  rfcObj,
  inputValues,
  activeTab,
  handleInputChange,
  UuidSmallFont,
}) => {
  let requiredPlans = ["pro", "basic"];
  const disabled =
    !requiredPlans.includes(rfcObj.plan_name) ||
    (rfcObj.plan_name === "basic" && activeTab === "cfdiEmitted");

  if (
    ["basic", "free"].includes(rfcObj.plan_name) &&
    activeTab === "cfdiEmitted"
  ) {
    requiredPlans = ["pro"];
  }

  const inputObj = (
    <Flex gap={0}>
      <div className={"label" + (disabled ? " disabled" : "")}>Folio</div>
      <IMaskInput
        style={{ width: "100%", fontSize: UuidSmallFont ? "10px" : null }}
        className={"input-uuid" + (disabled ? " disabled" : "")}
        placeholder="00000000-0000-0000-0000-000000000000"
        mask={"********{-}****{-}****{-}****{-}************"}
        name="uuid"
        value={inputValues.uuid}
        disabled={disabled}
        onAccept={(updatedValue) =>
          handleInputChange("uuid", {
            target: {
              name: "uuid",
              value: updatedValue.toUpperCase(),
            },
          })
        }
      />
    </Flex>
  );

  return disabled ? (
    <TooltipFeatureUnavailable requiredPlans={requiredPlans}>
      {inputObj}
    </TooltipFeatureUnavailable>
  ) : (
    inputObj
  );
};

const InputDateRange = ({
  rfcObj,
  inputValues,
  activeTab,
  smallView,
  handleInputChange,
}) => {
  let requiredPlans = ["pro", "basic"];
  const disabled =
    !requiredPlans.includes(rfcObj.plan_name) ||
    (rfcObj.plan_name === "basic" && activeTab === "cfdiEmitted");

  if (
    ["basic", "free"].includes(rfcObj.plan_name) &&
    activeTab === "cfdiEmitted"
  ) {
    requiredPlans = ["pro"];
  }

  const inputObj = (
    <RangePicker
      disabled={disabled}
      className={disabled ? " disabled" : ""}
      showTime
      style={{ width: "100%" }}
      placement={smallView ? "bottomRight" : "bottomLeft"}
      name="dates"
      value={[
        inputValues.date_from ? dayjs.unix(inputValues.date_from) : null,
        inputValues.date_to ? dayjs.unix(inputValues.date_to) : null,
      ]}
      onChange={(e) => handleInputChange("dates", e)}
      presets={[
        {
          label: "Hoy",
          value: () => [dayjs().startOf("d"), dayjs().endOf("d")],
        },
        {
          label: "Ayer",
          value: () => [
            dayjs().add(-1, "d").startOf("d"),
            dayjs().add(-1, "d").endOf("d"),
          ],
        },
        {
          label: "Esta semana",
          value: () => [dayjs().startOf("w"), dayjs().endOf("d")],
        },
        {
          label: "Últimos 7 días",
          value: () => [dayjs().add(-6, "d").startOf("d"), dayjs().endOf("d")],
        },
        {
          label: "Últimos 30 días",
          value: () => [dayjs().add(-29, "d").startOf("d"), dayjs().endOf("d")],
        },
        {
          label: "Este mes",
          value: () => [dayjs().startOf("M"), dayjs().endOf("d")],
        },
        {
          label: "Mes pasado",
          value: () => [
            dayjs().add(-1, "M").startOf("M"),
            dayjs().add(-1, "M").endOf("M"),
          ],
        },
        {
          label: "Este año",
          value: () => [dayjs().startOf("y"), dayjs().endOf("d")],
        },
        {
          label: "Año pasado",
          value: () => [
            dayjs().add(-1, "y").startOf("y"),
            dayjs().add(-1, "y").endOf("y"),
          ],
        },
      ]}
    />
  );

  return (
    <Space direction="vertical" size={0}>
      <Text className="date-range-label">Rango de fechas</Text>
      {disabled ? (
        <TooltipFeatureUnavailable requiredPlans={requiredPlans}>
          {inputObj}
        </TooltipFeatureUnavailable>
      ) : (
        inputObj
      )}
    </Space>
  );
};

const InputRfc = ({ rfcObj, inputValues, activeTab, handleInputChange }) => {
  const requiredPlans = ["pro"];
  const disabled = !requiredPlans.includes(rfcObj.plan_name);

  const partyPerspective = activeTab === "cfdiReceived" ? "Emisor" : "Receptor";
  const inputObj = (
    <Input
      addonBefore={`RFC ${partyPerspective}`}
      placeholder="XAXX010101000"
      count={{ max: 13 }}
      maxLength={13}
      style={{ width: "100%" }}
      disabled={disabled}
      name="rfc"
      value={inputValues.rfc}
      onInput={(e) => (e.target.value = e.target.value.toUpperCase())}
      onChange={(e) => handleInputChange("rfc", e)}
    />
  );

  return disabled ? (
    <TooltipFeatureUnavailable requiredPlans={requiredPlans}>
      {inputObj}
    </TooltipFeatureUnavailable>
  ) : (
    inputObj
  );
};

const InputName = ({ rfcObj, inputValues, activeTab, handleInputChange }) => {
  const requiredPlans = ["pro"];
  const disabled = !requiredPlans.includes(rfcObj.plan_name);

  const partyPerspective = activeTab === "cfdiReceived" ? "Emisor" : "Receptor";
  const inputObj = (
    <Input
      addonBefore={`Nombre ${partyPerspective}`}
      placeholder="Acme, SA de CV"
      count={{ max: 100 }}
      maxLength={100}
      style={{ width: "100%" }}
      disabled={disabled}
      name="name"
      value={inputValues.name}
      onInput={(e) => (e.target.value = e.target.value.toUpperCase())}
      onChange={(e) => handleInputChange("name", e)}
    />
  );

  return disabled ? (
    <TooltipFeatureUnavailable requiredPlans={requiredPlans}>
      {inputObj}
    </TooltipFeatureUnavailable>
  ) : (
    inputObj
  );
};

const InputAmount = ({ rfcObj, inputValues, handleInputChange, label }) => {
  const requiredPlans = ["pro"];
  const disabled = !requiredPlans.includes(rfcObj.plan_name);

  let label_var,
    min = Number.MIN_SAFE_INTEGER,
    max = Number.MAX_SAFE_INTEGER;
  if (label === "Monto mínimo") {
    label_var = "amount_min";
    if (inputValues["amount_max"]) {
      max = inputValues["amount_max"] - 0.01;
    }
  } else {
    label_var = "amount_max";
    if (inputValues["amount_min"]) {
      min = inputValues["amount_min"] + 0.01;
    }
  }

  const inputObj = (
    <InputNumber
      addonBefore={label}
      placeholder="$0"
      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
      parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
      style={{ width: "100%" }}
      disabled={disabled}
      name={label_var}
      value={inputValues[label_var] ? inputValues[label_var] : null}
      onChange={(e) => handleInputChange(label_var, e)}
      min={min}
      max={max}
    />
  );

  return disabled ? (
    <TooltipFeatureUnavailable requiredPlans={requiredPlans}>
      {inputObj}
    </TooltipFeatureUnavailable>
  ) : (
    inputObj
  );
};

const InputWordTags = ({ rfcObj, inputValues, handleInputChange }) => {
  const requiredPlans = ["pro"];
  const disabled = !requiredPlans.includes(rfcObj.plan_name);

  const selectObj = (
    <Select
      className="input-word-tags"
      mode="tags"
      // placeholder=""
      tokenSeparators={[","]}
      size="default"
      dropdownStyle={{ display: "none" }}
      style={{ width: "100%" }}
      maxTagCount={10}
      showSearch={false}
      suffixIcon={null}
      disabled={disabled}
      name="tags"
      value={inputValues.tags ? inputValues.tags : null}
      onChange={(e) => handleInputChange("tags", e)}
    />
  );

  return (
    <Space direction="vertical" size={0}>
      <Text>
        Palabras clave (se buscará en contenido de partidas, conceptos, etc.)
      </Text>
      {disabled ? (
        <TooltipFeatureUnavailable requiredPlans={requiredPlans}>
          {selectObj}
        </TooltipFeatureUnavailable>
      ) : (
        selectObj
      )}
      <Text style={{ fontStyle: "italic", color: "rgb(100,100,100)" }}>
        Separa las palabras clave con comas.
      </Text>
    </Space>
  );
};

export default function SearchBox({ rfcObj, activeTab }) {
  const { width } = useWindowDimensions();

  const smallView = width <= 880;
  const UuidSmallFont = width <= 450;

  const activeRfc = useStore((state) => state.activeRfc);
  const setCfdiSearchFilters = useStore((state) => state.setCfdiSearchFilters);

  const initialInputValues = {
    uuid: null,
    date_from: null,
    date_to: null,
    rfc: null,
    name: null,
    amount_min: null,
    amount_max: null,
    tags: null,
  };

  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);
  const [inputValues, setInputValues] = useState(initialInputValues);
  const [isInputValuesEmpty, setIsInputValuesEmpty] = useState(true);

  const handleFiltersSearchClick = () => {
    setCfdiSearchFilters(inputValues);
    setHasFiltersApplied(true);
  };

  const handleFiltersSearchClear = () => {
    setInputValues(initialInputValues);
    setCfdiSearchFilters(null);
    setHasFiltersApplied(false);
  };

  useEffect(() => {
    handleFiltersSearchClear();
  }, [activeRfc, activeTab]);

  useEffect(() => {
    const isEmpty = Object.values(inputValues).every(
      (x) => x === null || x === "",
    );
    setIsInputValuesEmpty(isEmpty);
    if (isEmpty) {
      setCfdiSearchFilters(null);
      setHasFiltersApplied(false);
    }
  }, [inputValues]);

  const handleInputChange = (type, e) => {
    let name, value;

    if (type === "dates") {
      setInputValues((prevState) => ({
        ...prevState,
        date_from: e ? getUnixFromDate(e[0].format()) : null,
        date_to: e ? getUnixFromDate(e[1].format()) : null,
      }));
    } else if (["amount_min", "amount_max", "tags"].includes(type)) {
      setInputValues((prevState) => ({
        ...prevState,
        [type]: e,
      }));
    } else {
      ({ name, value } = e.target);
      setInputValues((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const IconFiltersApplied = ({ hasFiltersApplied }) => {
    const { token: theme_token } = theme.useToken();

    return hasFiltersApplied ? (
      <Tooltip placement="right" title="Filtros aplicados">
        <FilterFilled style={{ color: theme_token.colorPrimary }} />
      </Tooltip>
    ) : null;
  };

  const items = [
    {
      key: "searchBox",
      label: (
        <Space>
          <SearchOutlined />
          Buscar CFDIs
          <IconFiltersApplied hasFiltersApplied={hasFiltersApplied} />
        </Space>
      ),
      children: (
        <Flex vertical gap={30}>
          <Flex vertical gap={10}>
            <InputUuid
              rfcObj={rfcObj}
              inputValues={inputValues}
              activeTab={activeTab}
              handleInputChange={handleInputChange}
              UuidSmallFont={UuidSmallFont}
            />
            <InputDateRange
              rfcObj={rfcObj}
              inputValues={inputValues}
              activeTab={activeTab}
              smallView={smallView}
              handleInputChange={handleInputChange}
            />
            <Flex vertical={smallView ? true : false} gap={10}>
              <InputRfc
                rfcObj={rfcObj}
                inputValues={inputValues}
                activeTab={activeTab}
                handleInputChange={handleInputChange}
              />
              <InputName
                rfcObj={rfcObj}
                inputValues={inputValues}
                activeTab={activeTab}
                handleInputChange={handleInputChange}
              />
            </Flex>
            <Flex vertical={smallView ? true : false} gap={10}>
              <InputAmount
                rfcObj={rfcObj}
                inputValues={inputValues}
                handleInputChange={handleInputChange}
                label="Monto mínimo"
              />
              <InputAmount
                rfcObj={rfcObj}
                inputValues={inputValues}
                handleInputChange={handleInputChange}
                label="Monto máximo"
              />
            </Flex>
            <InputWordTags
              rfcObj={rfcObj}
              inputValues={inputValues}
              handleInputChange={handleInputChange}
            />
          </Flex>
          <Flex gap={10}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleFiltersSearchClick}
              disabled={isInputValuesEmpty}
            >
              Buscar
            </Button>
            <Button
              icon={<DeleteOutlined />}
              onClick={handleFiltersSearchClear}
              disabled={isInputValuesEmpty}
            >
              Limpiar
            </Button>
          </Flex>
        </Flex>
      ),
    },
  ];

  return <Collapse expandIconPosition="end" items={items} />;
}
